import { LiPeopleType } from "@/enum/li-people.enum";

import type { CreateLiPeople, Suggestion } from "../schema/liPeople";
import type { LinkedinParams } from "./LinkedinTagAutocomplete";

/** The picked tags and people the form holds in local state, outside react-hook-form. */
export interface LiPeopleSelections {
	schools?: LinkedinParams[];
	locations?: LinkedinParams[];
	industries?: LinkedinParams[];
	serviceCategories?: LinkedinParams[];
	languages?: LinkedinParams[];
	currentCompaniesList?: LinkedinParams[];
	pastCompaniesList?: LinkedinParams[];
	followerOf?: LinkedinParams | null;
	connectionsOf?: LinkedinParams | null;
}

export type LiPeoplePayload = CreateLiPeople & {
	connectionsOf?: Suggestion | null;
};

const toSuggestions = (items?: LinkedinParams[]) =>
	items?.map((item) => ({ title: item.name, urn: item.id })) as Suggestion[];

const toCompanySuggestions = (items?: LinkedinParams[]) =>
	items?.map((item) => ({
		title: item.name,
		urn: item.id,
		image: item.image,
		subtitle: item.subtitle,
	})) as Suggestion[];

const toPerson = (person?: LinkedinParams | null) =>
	person?.id
		? ({
				urn: person.id,
				title: person.name,
				image: person.image || "",
				subtitle: person.subtitle || "",
			} as Suggestion)
		: null;

/**
 * The payload a source save sends: the form values plus the picked tags, and `urls` only for a
 * SPECIFIC source. A SEARCH source is scraped through its filters, and the form pre-fills `urls`
 * from the first entry, so sending it back would make the API scrape the old URL (PREC-4503).
 */
export function buildLiPeoplePayload(
	values: CreateLiPeople,
	selections: LiPeopleSelections
): LiPeoplePayload {
	return {
		...values,
		urls: values.type === LiPeopleType.SPECIFIC ? values.urls : undefined,
		schoolList: toSuggestions(selections.schools),
		locationList: toSuggestions(selections.locations),
		industryList: toSuggestions(selections.industries),
		serviceCategoryList: toSuggestions(selections.serviceCategories),
		languageList: toSuggestions(selections.languages),
		currentCompaniesList: toCompanySuggestions(selections.currentCompaniesList),
		pastCompaniesList: toCompanySuggestions(selections.pastCompaniesList),
		followerOf: toPerson(selections.followerOf),
		connectionsOf: toPerson(selections.connectionsOf),
	};
}
